import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { addDoc, collection, doc, getDoc, updateDoc } from 'firebase/firestore';
import { FiAlertCircle, FiLock } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { allows, rightsOf, useSessionUser } from '../../lib/permissions';
import { normalizeLinks } from '../../lib/links';
import { archiveDocuments, documentError, sendFiles } from '../../lib/documents';
import AttachmentsField, { useDocumentsStatus } from '../documents/AttachmentsField';
import DocumentViewer from '../documents/DocumentViewer';
import { usePatientDoctorContext } from '../PatientDoctorContext';
import { BUTTON, CARD, EmptyState, Field, MultiChips, Notice, PageHeader, Panel, Segmented, Select, SIZE, SkeletonRows, TextInput } from '../ui/kit';
import PatientPicker from '../patients/PatientPicker';
import { UNITS } from '../patients/patientModel';
import { ANESTHESIOLOGISTS, CEC_MANAGERS, DIAGNOSTICS, INDICATIONS, INSTRUMENTALISTS, SURGEONS, TYPE_OPTIONS, croAccess, toForm, toRecord } from './croModel';

const CroForm = () => {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const user = useSessionUser();
  const access = croAccess(user);
  const { ensurePatients } = usePatientDoctorContext();

  const [status, setStatus] = useState(editing ? 'loading' : 'ready');
  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState(() => ({ ...toForm({}), datePatient: new Date().toISOString().slice(0, 10), unit: user.service || '' }));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Files chosen on the device (sent on save), documents taken out, document opened.
  const [pending, setPending] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [sending, setSending] = useState(null);
  const [viewing, setViewing] = useState(null);
  const documentsStatus = useDocumentsStatus();

  useEffect(() => {
    ensurePatients().catch(() => {});
    if (!editing) return undefined;
    let cancelled = false;
    getDoc(doc(db, 'cros', id))
      .then((snapshot) => {
        if (cancelled) return;
        if (!snapshot.exists()) {
          setStatus('missing');
          return;
        }
        const cro = { id: snapshot.id, ...snapshot.data() };
        setOriginal(cro);
        setForm(toForm(cro));
        setStatus('ready');
      })
      .catch(() => !cancelled && setStatus('error'));
    return () => {
      cancelled = true;
    };
  }, [editing, id, ensurePatients]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const set = (name, value) => {
    setDirty(true);
    setError('');
    setForm((current) => ({ ...current, [name]: value }));
  };

  // As before, a report is written for a patient of the unit.
  const inMyUnits = useCallback(
    (patient) => access.isAdmin || access.myUnits.includes(String(patient.services || '').toLowerCase()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [access.isAdmin, user.service]
  );

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (!form.patientId) {
      setError('Choisissez le dossier du patient opéré.');
      return;
    }
    if (!form.datePatient) {
      setError('Indiquez la date de l’intervention.');
      return;
    }
    if (form.type === 'Autres' && !form.customComment.trim()) {
      setError('Précisez le commentaire (type « Autre »).');
      return;
    }
    setSaving(true);
    setError('');
    const unit = form.unit || user.service || '';
    let documents = form.documents;
    try {
      // Files sent first: once in the service's Dropbox, they stay listed if a later step fails.
      if (pending.length) {
        try {
          documents = [...documents, ...(await sendFiles(pending, { section: 'cro', patientId: form.patientId, unit }, setSending))];
          setPending([]);
          setForm((current) => ({ ...current, documents }));
        } catch (sendError) {
          const sent = sendError.sent || [];
          documents = [...documents, ...sent];
          setForm((current) => ({ ...current, documents }));
          setPending((current) => current.slice(sent.length));
          throw sendError;
        } finally {
          setSending(null);
        }
      }
      if (removed.length) {
        const failed = await archiveDocuments(removed);
        setRemoved([]);
        if (failed.length) {
          documents = [...documents, ...failed];
          setForm((current) => ({ ...current, documents }));
          throw new Error('archive');
        }
      }
      const record = toRecord({ ...form, documents, unit });
      if (editing) {
        await updateDoc(doc(db, 'cros', id), { ...record, statut: (original && original.statut) || 'Validé', updatedBy: user.name || '', updatedAt: new Date().toISOString() });
        setDirty(false);
        navigate(`/cro/${id}`, { replace: true, state: { saved: true } });
      } else {
        const created = await addDoc(collection(db, 'cros'), { ...record, statut: 'Validé', createdBy: user.name || '', createdAt: new Date().toISOString() });
        setDirty(false);
        navigate(`/cro/${created.id}`, { replace: true, state: { saved: true } });
      }
    } catch (err) {
      setError(
        err.message === 'archive'
          ? 'Un document n’a pas pu être retiré. Réessayez.'
          : await documentError(err, 'Le compte rendu n’a pas pu être enregistré. Vérifiez la connexion, puis réessayez.')
      );
    } finally {
      setSaving(false);
    }
  };

  // Taking a stored document out of the report sends it to the archives: deletion level.
  const canRemoveStored = editing ? Boolean(original) && access.canDelete(original) : allows(rightsOf(user), 'cro', 'supprimer');
  const removeStored = (item) => {
    setDirty(true);
    setForm((current) => ({ ...current, documents: current.documents.filter((entry) => entry.id !== item.id) }));
    setRemoved((current) => [...current, item]);
  };

  const cancel = () => {
    if (dirty && !window.confirm('Abandonner les modifications non enregistrées ?')) return;
    setDirty(false);
    navigate(editing ? `/cro/${id}` : '/cro');
  };

  const back = editing ? { to: `/cro/${id}`, label: 'Retour au compte rendu' } : { to: '/cro', label: 'Comptes rendus opératoires' };
  const shell = (content) => <main className="mx-auto max-w-4xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">{content}</main>;

  const allowed = editing ? original && access.canEdit(original) : access.canCreate;
  if (status === 'ready' && !allowed) {
    return shell(
      <>
        <PageHeader back={back} title="Saisie impossible" />
        <div className={`${CARD} mt-8`}>
          <EmptyState icon={FiLock} title="Vos droits ne permettent pas de rédiger ce compte rendu.">
            Les comptes rendus opératoires sont rédigés par les chirurgiens de l’unité. Demandez à l’administrateur si vous en avez besoin.
          </EmptyState>
        </div>
      </>
    );
  }
  if (status !== 'ready') {
    return shell(
      <>
        <PageHeader back={back} title={status === 'loading' ? 'Chargement…' : 'Compte rendu introuvable'} />
        <div className={`${CARD} mt-8 ${status === 'loading' ? 'p-6' : ''}`}>
          {status === 'loading' ? <SkeletonRows rows={4} /> : <EmptyState icon={FiAlertCircle} title="Le compte rendu n’a pas pu être chargé." />}
        </div>
      </>
    );
  }

  const unitChoices = access.isAdmin ? UNITS : access.myUnits.length > 1 ? UNITS.filter((unit) => access.myUnits.includes(unit.toLowerCase())) : [];
  const people = [
    ['surgeons', 'Chirurgiens', SURGEONS, 'Autre chirurgien…'],
    ['anesthesiologists', 'Anesthésistes', ANESTHESIOLOGISTS, 'Autre anesthésiste…'],
    ['cecManagers', 'Responsables CEC', CEC_MANAGERS, 'Autre responsable CEC…'],
    ['instrumentalists', 'Instrumentistes', INSTRUMENTALISTS, 'Autre instrumentiste…'],
  ];
  const links = normalizeLinks(form.links);

  return shell(
    <form onSubmit={submit} noValidate>
      <PageHeader back={back} eyebrow={editing ? 'Modifier le compte rendu' : 'Nouveau compte rendu'} title="Compte rendu opératoire" />

      <div className="mt-8 flex flex-col gap-[16px]">
        <Panel title="Patient et intervention">
          <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-[1fr_200px]">
            <Field id="cro-patient" label="Dossier patient" required hint={access.isAdmin ? '' : 'Patients de votre unité.'}>
              <PatientPicker id="cro-patient" single filter={inMyUnits} values={form.patientId ? [form.patientId] : []} onChange={(ids) => set('patientId', ids[0] || '')} />
            </Field>
            <Field id="cro-date" label="Date de l’intervention" required>
              <TextInput id="cro-date" type="date" value={form.datePatient} onChange={(e) => set('datePatient', e.target.value)} />
            </Field>
            {unitChoices.length > 0 && (
              <Field id="cro-unit" label="Unité">
                <Select id="cro-unit" value={form.unit} onChange={(e) => set('unit', e.target.value)} options={form.unit && !unitChoices.includes(form.unit) ? [form.unit, ...unitChoices] : unitChoices} />
              </Field>
            )}
          </div>
        </Panel>

        <Panel title="Diagnostic et indication">
          <div className="flex flex-col gap-[18px]">
            <Field id="cro-indications" label="Indication opératoire">
              <MultiChips id="cro-indications" options={INDICATIONS} values={form.indications} onChange={(value) => set('indications', value)} customPlaceholder="Autre indication…" />
            </Field>
            <Field id="cro-diagnostics" label="Diagnostic">
              <MultiChips id="cro-diagnostics" options={DIAGNOSTICS} values={form.diagnostics} onChange={(value) => set('diagnostics', value)} customPlaceholder="Autre diagnostic…" />
            </Field>
          </div>
        </Panel>

        <Panel title="Équipe opératoire">
          <div className="flex flex-col gap-[18px]">
            {people.map(([name, label, options, placeholder]) => (
              <Field key={name} id={`cro-${name}`} label={label}>
                <MultiChips id={`cro-${name}`} options={options} values={form[name]} onChange={(value) => set(name, value)} customPlaceholder={placeholder} />
              </Field>
            ))}
            <Field id="cro-interne" label="Interne / DES" hint="Nom de l’interne ou du DES qui a participé, s’il y a lieu.">
              <TextInput id="cro-interne" value={form.interne} onChange={(e) => set('interne', e.target.value)} className="max-w-[420px]" />
            </Field>
          </div>
        </Panel>

        <Panel title="Commentaire">
          <div className="flex flex-col gap-[14px]">
            <Segmented id="cro-type" value={form.type} onChange={(value) => set('type', value)} options={TYPE_OPTIONS} allowEmpty={false} />
            {form.type === 'Autres' && (
              <TextInput id="cro-comment" aria-label="Commentaire" value={form.customComment} onChange={(e) => set('customComment', e.target.value)} placeholder="Précisez le commentaire" />
            )}
          </div>
        </Panel>

        <Panel title="Documents">
          <AttachmentsField
            id="cro-documents"
            status={documentsStatus}
            stored={form.documents}
            onOpen={setViewing}
            onRemoveStored={canRemoveStored && !saving ? removeStored : null}
            links={links}
            onLinksChange={saving ? null : (next) => set('links', next.map((item) => item.link))}
            pending={pending}
            onPendingChange={(next) => {
              setDirty(true);
              setPending(next);
            }}
            sending={sending}
          />
          {removed.length > 0 && (
            <p className="mb-0 mt-[12px] text-[12.5px] text-black/45">
              {removed.length === 1 ? 'Un document sera retiré' : `${removed.length} documents seront retirés`} à l’enregistrement (conservés dans les archives du
              Dropbox du service).
            </p>
          )}
        </Panel>

        {viewing && (
          <DocumentViewer
            item={viewing}
            onClose={() => setViewing(null)}
            canReplace={editing && Boolean(original) && access.canEdit(original)}
            canDownload={access.canExport}
          />
        )}
      </div>

      {error && (
        <div className="mt-6">
          <Notice tone="red" icon={FiAlertCircle}>
            {error}
          </Notice>
        </div>
      )}

      <div className="sticky bottom-0 z-10 -mx-[20px] mt-8 border-0 border-t-[1px] border-solid border-black/[0.06] bg-mist/90 px-[20px] py-[14px] backdrop-blur-xl sm:-mx-8 sm:px-8 lg:-mx-10 lg:px-10">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          {dirty && <span className="text-center text-[13px] text-black/45 sm:mr-auto sm:text-left">Modifications non enregistrées</span>}
          <button type="button" onClick={cancel} className={`${BUTTON.secondary} ${SIZE.md}`}>
            Annuler
          </button>
          <button type="submit" disabled={saving} className={`${BUTTON.primary} ${SIZE.md} min-w-[180px]`}>
            {sending
              ? `Envoi des documents ${sending.index + 1}/${pending.length}…`
              : saving
                ? 'Enregistrement…'
                : editing
                  ? 'Enregistrer les modifications'
                  : 'Enregistrer le compte rendu'}
          </button>
        </div>
      </div>
      {!editing && (
        <p className="mb-0 mt-[12px] text-center text-[12.5px] text-black/40">
          <Link to="/cro" className="text-black/50 underline hover:text-ink">
            Retour à la liste sans enregistrer
          </Link>
        </p>
      )}
    </form>
  );
};

export default CroForm;
